<script setup>
import { computed } from 'vue';
import { classesTag, SEVERIDADE_PARA_COR } from '@/commons/tags';

/**
 * Tag padrão do sistema (estilo "soft": fundo claro + texto colorido).
 *
 * Formas de definir a cor (em ordem de prioridade):
 *  - corHex: fundo dinâmico via hexadecimal (texto branco) — ex.: rótulos vindos da API.
 *  - cor: chave semântica de CORES_TAG (verde, vermelho, amarelo, roxo, azul, cyan,
 *         indigo, laranja, rosa, cinza, primary).
 *  - severidade: severidade nativa do PrimeVue (success, warning, danger, secondary…),
 *         convertida para a cor semântica correspondente.
 *  - nenhuma: cinza (neutro).
 */
const props = defineProps({
    valor: {
        type: [String, Number],
        default: '',
    },
    cor: {
        type: String,
        default: null,
    },
    severidade: {
        type: String,
        default: null,
    },
    corHex: {
        type: String,
        default: null,
    },
    icone: {
        type: String,
        default: null,
    },
    negrito: {
        type: Boolean,
        default: false,
    },
    arredondado: {
        type: Boolean,
        default: false,
    },
    // Classes adicionais aplicadas ao root (ex.: 'ml-1', 'capitalize', 'w-max')
    classe: {
        type: String,
        default: '',
    },
    tooltip: {
        type: String,
        default: '',
    },
});

const corResolvida = computed(() => {
    if (props.cor) return props.cor;
    if (props.severidade) return SEVERIDADE_PARA_COR[props.severidade] ?? null;
    return null;
});

const classes = computed(() => {
    const arredondamento = props.arredondado ? 'border-round-3xl' : '';
    let base;
    if (props.corHex) {
        base = `border-round-md ${props.negrito ? 'font-semibold' : 'font-medium'} text-white`;
    } else if (!corResolvida.value) {
        base = 'border-round-md font-medium';
    } else {
        base = classesTag(corResolvida.value, { negrito: props.negrito });
    }
    return `${base} ${arredondamento} ${props.classe}`.replace(/\s+/g, ' ').trim();
});

const estiloRoot = computed(() => {
    const base = 'font-size: 11px;';
    if (props.corHex) return `${base} background: ${props.corHex} !important;`;
    return base;
});
</script>

<template>
    <Tag :value="valor" v-tooltip.top="tooltip" :pt="{
        root: { class: classes, style: estiloRoot },
        icon: { style: 'font-size: 5px; line-height: 1.5;' },
    }">
        <template v-if="icone" #icon>
            <i :class="['pi', icone, 'mr-2']" :style="`font-size: ${icone === 'pi-circle-fill' ? 6 : 10}px; line-height: 1.5;`" />
        </template>
        <slot />
    </Tag>
</template>
